/**
 * DevToolsPanel.jsx - Developer Cheats Panel
 *
 * A compact, grid-based testing panel for debugging game systems.
 * Toggle with F9 key.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useGame } from '../../context/GameContext';
import { resources } from '../../data';
import {
  WEAPONS,
  HELMETS,
  ARMOR,
  OFFHAND,
  GLOVES,
  BOOTS,
  CAPES,
  BOTTOMS,
  BELTS,
  PENDANTS,
  RINGS,
  getEquipmentById,
} from '../../data/dungeon/equipment';
import { DUNGEON_RESOURCES } from '../../data/dungeon/resources';
import { CONSUMABLES, BUFF_ITEMS, GEMSTONE_ITEMS, MISC_ITEMS, CUBE_ITEMS } from '../../data/dungeon/items';
import { LOOTBOXES } from '../../data/dungeon/items/lootboxes';
import { BOSS_CHESTS } from '../../data/dungeon/items/bossChests';
import './DevToolsPanel.css';

// Collect all equipment into categories
const EQUIPMENT_CATEGORIES = {
  weapons: WEAPONS,
  helmets: HELMETS,
  armor: ARMOR,
  offhand: OFFHAND,
  gloves: GLOVES,
  boots: BOOTS,
  capes: CAPES,
  bottoms: BOTTOMS,
  belts: BELTS,
  pendants: PENDANTS,
  rings: RINGS,
};

const RARITY_ORDER = ['common', 'uncommon', 'rare', 'epic', 'legendary'];

// Dungeon tier configs for dev testing
const DUNGEON_TIERS = {
  tier1: {
    name: 'Tier 1',
    label: '🏚️ Ancient Ruins',
    mobPool: ['goblin', 'skeleton', 'wolf'],
    bossPool: ['boss_ogre'],
  },
  tier2: {
    name: 'Tier 2',
    label: '⚰️ Dark Catacombs',
    mobPool: ['skeleton', 'wraith', 'orc', 'darkKnight'],
    bossPool: ['boss_lich'],
  },
  tier3: {
    name: 'Tier 3',
    label: '🛕 Corrupted Temple',
    mobPool: ['orc', 'wraith', 'darkKnight', 'demon', 'vampire'],
    bossPool: ['boss_dragon'],
  },
  tier4: {
    name: 'Tier 4',
    label: '🕳️ Abyssal Depths',
    mobPool: ['voidStalker', 'abyssalKnight', 'shadowLord', 'demon', 'vampire'],
    bossPool: ['boss_abyssalLord'],
  },
  tier5: {
    name: 'Tier 5',
    label: '🌌 Void Citadel',
    mobPool: ['cosmicHorror', 'eternityWarden', 'voidEmperor', 'voidStalker', 'shadowLord'],
    bossPool: ['boss_voidGod'],
  },
};

function DevToolsPanel({ isOpen, onClose }) {
  const {
    playerResources,
    setPlayerResources,
    worldMap,
    setWorldMap,
    playerCharacter,
    modifyStarEssence,
    addToInventory,
    addExperience,
    allocateStatPoint,
    researchedTechs,
    completedQuests,
    builtWonders,
    devCooldownsDisabled,
    setDevCooldownsDisabled,
    devTeleportMode,
    setDevTeleportMode,
    devResearchSpeedMultiplier,
    setDevResearchSpeedMultiplier,
    enterDungeon,
  } = useGame();

  const [activeTab, setActiveTab] = useState('quick');

  // Handle escape to close
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // === RESOURCE FUNCTIONS ===
  const giveAllResources = useCallback((amount) => {
    setPlayerResources(prev => {
      const updated = { ...prev };
      Object.keys(resources).forEach(resId => {
        updated[resId] = (updated[resId] || 0) + amount;
      });
      return updated;
    });
  }, [setPlayerResources]);

  const setAllResources = useCallback((amount) => {
    setPlayerResources(prev => {
      const updated = { ...prev };
      Object.keys(resources).forEach(resId => {
        updated[resId] = amount;
      });
      return updated;
    });
  }, [setPlayerResources]);

  // === EQUIPMENT FUNCTIONS ===
  const spawnEquipment = useCallback((equipId, maxQuality = false) => {
    const baseItem = getEquipmentById(equipId);
    if (!baseItem) return;
    const quality = maxQuality ? 1.25 : 0.5 + Math.random() * 0.75;
    const item = {
      ...baseItem,
      instanceId: `${equipId}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      quality,
    };
    addToInventory(item);
  }, [addToInventory]);

  const spawnAllOfRarity = useCallback((rarity) => {
    Object.values(EQUIPMENT_CATEGORIES).forEach(categoryItems => {
      Object.values(categoryItems)
        .filter(item => item.rarity === rarity)
        .forEach(item => spawnEquipment(item.id));
    });
  }, [spawnEquipment]);

  // === MATERIAL FUNCTIONS ===
  const spawnMaterial = useCallback((materialId, amount = 10) => {
    const material = DUNGEON_RESOURCES[materialId];
    if (!material) return;
    const item = {
      ...material,
      instanceId: `material_${materialId}`,
      category: 'material',
      quantity: amount,
    };
    addToInventory(item, amount);
  }, [addToInventory]);

  const spawnAllMaterials = useCallback((amount = 50) => {
    Object.values(DUNGEON_RESOURCES).forEach(mat => spawnMaterial(mat.id, amount));
  }, [spawnMaterial]);

  // === GEMSTONE FUNCTIONS ===
  const spawnGemstone = useCallback((gemstoneId, amount = 50) => {
    const gemstone = GEMSTONE_ITEMS[gemstoneId];
    if (!gemstone) return;
    const item = {
      ...gemstone,
      instanceId: `gemstone_${gemstoneId}`,
      quantity: amount,
    };
    addToInventory(item, amount);
  }, [addToInventory]);

  const spawnAllGemstones = useCallback((amount = 100) => {
    Object.keys(GEMSTONE_ITEMS).forEach(gemId => spawnGemstone(gemId, amount));
  }, [spawnGemstone]);

  // === CUBE FUNCTIONS ===
  const spawnCube = useCallback((cubeId, amount = 10) => {
    const cube = CUBE_ITEMS[cubeId];
    if (!cube) return;
    const item = {
      ...cube,
      instanceId: `cube_${cubeId}`,
      quantity: amount,
    };
    addToInventory(item, amount);
  }, [addToInventory]);

  const spawnAllCubes = useCallback((amount = 20) => {
    Object.keys(CUBE_ITEMS).forEach(cubeId => spawnCube(cubeId, amount));
  }, [spawnCube]);

  // === CONSUMABLE FUNCTIONS ===
  const spawnConsumable = useCallback((itemId, amount = 10) => {
    const item = CONSUMABLES[itemId] || BUFF_ITEMS[itemId] || MISC_ITEMS[itemId];
    if (!item) return;
    const consumable = {
      ...item,
      instanceId: `${itemId}_${Date.now()}`,
      quantity: amount,
    };
    addToInventory(consumable, amount);
  }, [addToInventory]);

  // === CHEST FUNCTIONS ===
  const spawnChest = useCallback((chestId, dungeonLevel = null) => {
    // Check both regular lootboxes and boss chests
    const chest = LOOTBOXES[chestId] || BOSS_CHESTS[chestId];
    if (!chest) return;
    const chestInstance = {
      ...chest,
      instanceId: `${chestId}_${Date.now()}_${Math.floor(Math.random() * 10000)}`,
      quantity: 1,
      dungeonLevel: dungeonLevel || chest.dungeonLevel || 10,
    };
    addToInventory(chestInstance, 1);
  }, [addToInventory]);

  // === WORLD FUNCTIONS ===
  const resetCooldowns = useCallback(() => {
    setWorldMap(prev => ({
      ...prev,
      lastMoveTime: null,
      lastGatherTime: null,
      dungeonCooldowns: {},
    }));
  }, [setWorldMap]);

  const discoverTiles = useCallback((radius) => {
    const { q: currentQ, r: currentR } = worldMap.discoveredNodes[worldMap.currentNode]?.coordinates || { q: 0, r: 0 };
    setWorldMap(prev => {
      const newNodes = { ...prev.discoveredNodes };
      for (let dq = -radius; dq <= radius; dq++) {
        for (let dr = -radius; dr <= radius; dr++) {
          if (Math.abs(dq + dr) <= radius) {
            const q = currentQ + dq;
            const r = currentR + dr;
            const nodeId = `node_${q}_${r}`;
            if (!newNodes[nodeId] && !(q === 0 && r === 0)) {
              newNodes[nodeId] = {
                id: nodeId,
                coordinates: { q, r },
                biome: 'plains',
                discovered: true,
                tileQuality: 1.0,
              };
            }
          }
        }
      }
      return { ...prev, discoveredNodes: newNodes };
    });
  }, [worldMap, setWorldMap]);

  if (!isOpen) return null;

  // Computed values
  const inventoryCount = playerCharacter?.inventory?.length || 0;
  const tilesCount = Object.keys(worldMap?.discoveredNodes || {}).length;

  return (
    <div className="dev-overlay" onClick={onClose}>
      <div className="dev-panel" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="dev-header">
          <span className="dev-title">🔧 Dev Tools</span>
          <span className="dev-hint">F9 / ESC</span>
          <button className="dev-close" onClick={onClose}>✕</button>
        </div>

        {/* Tabs */}
        <div className="dev-tabs">
          {[
            { id: 'quick', icon: '⚡', label: 'Quick' },
            { id: 'resources', icon: '💰', label: 'Resources' },
            { id: 'items', icon: '⚔️', label: 'Items' },
            { id: 'player', icon: '👤', label: 'Player' },
            { id: 'world', icon: '🗺️', label: 'World' },
          ].map(tab => (
            <button
              key={tab.id}
              className={`dev-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.icon}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="dev-content">
          {/* === QUICK TAB === */}
          {activeTab === 'quick' && (
            <div className="dev-grid">
              {/* Toggles */}
              <div className="dev-card toggles">
                <div className="card-title">Toggles</div>
                <div className="toggle-row">
                  <span className={devCooldownsDisabled ? 'active' : ''}>
                    {devCooldownsDisabled ? '⚡ No Cooldowns' : '⏱️ Cooldowns'}
                  </span>
                  <button
                    className={`toggle-btn ${devCooldownsDisabled ? 'on' : ''}`}
                    onClick={() => setDevCooldownsDisabled(!devCooldownsDisabled)}
                  />
                </div>
                <div className="toggle-row">
                  <span className={devTeleportMode ? 'active' : ''}>
                    {devTeleportMode ? '🌀 Teleport' : '🚶 Walk'}
                  </span>
                  <button
                    className={`toggle-btn ${devTeleportMode ? 'on' : ''}`}
                    onClick={() => setDevTeleportMode(!devTeleportMode)}
                  />
                </div>
                <div className="toggle-row">
                  <span className={devResearchSpeedMultiplier > 1 ? 'active' : ''}>
                    📚 Research {devResearchSpeedMultiplier > 1 ? `${devResearchSpeedMultiplier}×` : '1×'}
                  </span>
                  <select
                    className="dev-select"
                    value={devResearchSpeedMultiplier}
                    onChange={(e) => setDevResearchSpeedMultiplier(Number(e.target.value))}
                  >
                    <option value={1}>1× (Normal)</option>
                    <option value={10}>10× Fast</option>
                    <option value={100}>100× Turbo</option>
                    <option value={1000}>1000× Instant</option>
                  </select>
                </div>
              </div>

              {/* Quick Resources */}
              <div className="dev-card">
                <div className="card-title">Resources</div>
                <div className="btn-grid cols-2">
                  <button className="dev-btn" onClick={() => giveAllResources(10000)}>+10k All</button>
                  <button className="dev-btn" onClick={() => giveAllResources(100000)}>+100k All</button>
                  <button className="dev-btn" onClick={() => setAllResources(0)}>Zero</button>
                  <button className="dev-btn primary" onClick={() => setAllResources(1000000)}>1M All</button>
                </div>
              </div>

              {/* Quick Items */}
              <div className="dev-card">
                <div className="card-title">Items</div>
                <div className="btn-grid cols-2">
                  <button className="dev-btn" onClick={() => spawnAllMaterials(50)}>+50 Mats</button>
                  <button className="dev-btn" onClick={() => spawnAllGemstones(100)}>+100 Gems</button>
                  <button className="dev-btn cube" onClick={() => spawnAllCubes(20)}>+20 Cubes</button>
                  <button className="dev-btn legendary" onClick={() => spawnAllOfRarity('legendary')}>Legendary</button>
                </div>
              </div>

              {/* Quick Player */}
              <div className="dev-card">
                <div className="card-title">Player</div>
                <div className="btn-grid cols-2">
                  <button className="dev-btn" onClick={() => addExperience(10000)}>+10k XP</button>
                  <button className="dev-btn" onClick={() => addExperience(100000)}>+100k XP</button>
                  <button className="dev-btn star" onClick={() => modifyStarEssence(1000, false)}>+1k ✦</button>
                  <button className="dev-btn star" onClick={() => modifyStarEssence(10000, false)}>+10k ✦</button>
                </div>
              </div>

              {/* Quick World */}
              <div className="dev-card">
                <div className="card-title">World</div>
                <div className="btn-grid cols-2">
                  <button className="dev-btn" onClick={resetCooldowns}>Reset CDs</button>
                  <button className="dev-btn" onClick={() => discoverTiles(5)}>+5 Tiles</button>
                  <button className="dev-btn" onClick={() => discoverTiles(10)}>+10 Tiles</button>
                  <button className="dev-btn" onClick={() => setWorldMap(prev => ({ ...prev, currentNode: 'kingdom' }))}>
                    → Kingdom
                  </button>
                </div>
              </div>

              {/* Quick Dungeon */}
              <div className="dev-card">
                <div className="card-title">Dungeon</div>
                <div className="btn-grid cols-1">
                  {Object.entries(DUNGEON_TIERS).map(([tierId, tier]) => (
                    <button
                      key={tierId}
                      className={`dev-btn dungeon-${tierId}`}
                      onClick={() => {
                        enterDungeon(tierId, tier.mobPool, tier.bossPool, null);
                        onClose();
                      }}
                      disabled={playerCharacter?.isInDungeon}
                    >
                      {tier.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Stats Display */}
              <div className="dev-card stats">
                <div className="stat-row"><span>Level</span><span>{playerCharacter?.level || 1}</span></div>
                <div className="stat-row"><span>Inventory</span><span>{inventoryCount}/200</span></div>
                <div className="stat-row"><span>Tiles</span><span>{tilesCount}</span></div>
                <div className="stat-row"><span>✦ Essence</span><span>{(playerCharacter?.starEssence || 0).toLocaleString()}</span></div>
              </div>
            </div>
          )}

          {/* === RESOURCES TAB === */}
          {activeTab === 'resources' && (
            <div className="dev-section">
              <div className="dev-card wide">
                <div className="card-title">Quick Set All</div>
                <div className="btn-grid cols-5">
                  <button className="dev-btn" onClick={() => setAllResources(0)}>Zero</button>
                  <button className="dev-btn" onClick={() => setAllResources(1000)}>1k</button>
                  <button className="dev-btn" onClick={() => setAllResources(10000)}>10k</button>
                  <button className="dev-btn" onClick={() => setAllResources(100000)}>100k</button>
                  <button className="dev-btn primary" onClick={() => setAllResources(1000000)}>1M</button>
                </div>
              </div>
              <div className="resource-grid">
                {Object.values(resources).map(res => (
                  <button
                    key={res.id}
                    className="resource-btn"
                    onClick={() => setPlayerResources(prev => ({ ...prev, [res.id]: (prev[res.id] || 0) + 1000 }))}
                    title={`${res.name}: ${playerResources[res.id] || 0}`}
                  >
                    <span className="res-icon">{res.icon}</span>
                    <span className="res-count">{Math.floor((playerResources[res.id] || 0) / 1000)}k</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* === ITEMS TAB === */}
          {activeTab === 'items' && (
            <div className="dev-section">
              {/* Equipment by Rarity */}
              <div className="dev-card wide">
                <div className="card-title">Spawn Equipment</div>
                <div className="btn-grid cols-5">
                  {RARITY_ORDER.map(rarity => (
                    <button
                      key={rarity}
                      className={`dev-btn ${rarity}`}
                      onClick={() => spawnAllOfRarity(rarity)}
                    >
                      {rarity.slice(0, 3).toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>

              {/* Special Items */}
              <div className="dev-card wide">
                <div className="card-title">Special Items</div>
                <div className="btn-grid cols-4">
                  <button className="dev-btn legendary" onClick={() => spawnEquipment('firebrand', true)}>🔥 Firebrand</button>
                  <button className="dev-btn legendary" onClick={() => spawnEquipment('frostblade', true)}>❄️ Frostblade</button>
                  <button className="dev-btn legendary" onClick={() => spawnEquipment('natureweaver', true)}>🌿 Nature</button>
                  <button className="dev-btn epic" onClick={() => spawnConsumable('cleanSlateScroll', 5)}>📜 Clean Slate</button>
                </div>
              </div>

              {/* Materials & Consumables */}
              <div className="dev-card wide">
                <div className="card-title">Materials & Consumables</div>
                <div className="btn-grid cols-4">
                  <button className="dev-btn" onClick={() => spawnAllMaterials(10)}>+10 All Mats</button>
                  <button className="dev-btn" onClick={() => spawnAllMaterials(50)}>+50 All Mats</button>
                  <button className="dev-btn" onClick={() => spawnConsumable('superiorHealthPotion', 10)}>+10 HP Pots</button>
                  <button className="dev-btn" onClick={() => spawnConsumable('elixirOfPower', 5)}>+5 Elixirs</button>
                </div>
              </div>

              {/* Gemstones */}
              <div className="dev-card wide gems">
                <div className="card-title">💎 Gemstones</div>
                <div className="gem-display">
                  {Object.entries(GEMSTONE_ITEMS).map(([gemId, gem]) => {
                    const count = playerCharacter?.inventory?.reduce((acc, item) => {
                      if (item?.isGemstone && item.id === gemId) return acc + (item.quantity || 1);
                      return acc;
                    }, 0) || 0;
                    return (
                      <button
                        key={gemId}
                        className={`gem-btn ${gemId}`}
                        onClick={() => spawnGemstone(gemId, 50)}
                        title={`+50 ${gem.name}`}
                      >
                        {gem.icon} {count}
                      </button>
                    );
                  })}
                </div>
                <div className="btn-grid cols-3">
                  <button className="dev-btn" onClick={() => spawnAllGemstones(50)}>+50 All</button>
                  <button className="dev-btn" onClick={() => spawnAllGemstones(200)}>+200 All</button>
                  <button className="dev-btn primary" onClick={() => spawnAllGemstones(1000)}>+1k All</button>
                </div>
              </div>

              {/* Cubes */}
              <div className="dev-card wide cubes">
                <div className="card-title">🎲 Cubes</div>
                <div className="cube-display">
                  {Object.entries(CUBE_ITEMS).map(([cubeId, cube]) => {
                    const count = playerCharacter?.inventory?.reduce((acc, item) => {
                      if (item?.isCube && item.id === cubeId) return acc + (item.quantity || 1);
                      return acc;
                    }, 0) || 0;
                    return (
                      <button
                        key={cubeId}
                        className={`cube-btn ${cubeId}`}
                        onClick={() => spawnCube(cubeId, 10)}
                        title={`+10 ${cube.name}`}
                      >
                        {cube.icon} {count}
                      </button>
                    );
                  })}
                </div>
                <div className="btn-grid cols-3">
                  <button className="dev-btn" onClick={() => spawnAllCubes(10)}>+10 All</button>
                  <button className="dev-btn" onClick={() => spawnAllCubes(50)}>+50 All</button>
                  <button className="dev-btn cube" onClick={() => spawnAllCubes(100)}>+100 All</button>
                </div>
              </div>

              {/* Chests */}
              <div className="dev-card wide chests">
                <div className="card-title">📦 Dungeon Chests</div>
                <div className="btn-grid cols-5">
                  <button className="dev-btn common" onClick={() => spawnChest('chest_tier1')} title="Tier 1 - Level 10">
                    T1 📦
                  </button>
                  <button className="dev-btn uncommon" onClick={() => spawnChest('chest_tier2')} title="Tier 2 - Level 25">
                    T2 🗃️
                  </button>
                  <button className="dev-btn rare" onClick={() => spawnChest('chest_tier3')} title="Tier 3 - Level 45">
                    T3 📿
                  </button>
                  <button className="dev-btn epic" onClick={() => spawnChest('chest_tier4')} title="Tier 4 - Level 65">
                    T4 🎁
                  </button>
                  <button className="dev-btn legendary" onClick={() => spawnChest('chest_tier5')} title="Tier 5 - Level 85">
                    T5 🌌
                  </button>
                </div>
                <div className="btn-grid cols-2">
                  <button className="dev-btn epic" onClick={() => spawnChest('chest_boss')} title="Boss Treasure">
                    👑 Boss Chest
                  </button>
                  <button className="dev-btn legendary" onClick={() => spawnChest('chest_golden')} title="Golden Chest">
                    ✨ Golden Chest
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* === PLAYER TAB === */}
          {activeTab === 'player' && (
            <div className="dev-section">
              {/* Player Info */}
              <div className="dev-card wide info">
                <div className="info-grid">
                  <div className="info-item">
                    <span className="info-label">Level</span>
                    <span className="info-value">{playerCharacter?.level || 1}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">XP</span>
                    <span className="info-value">{playerCharacter?.experience || 0}/{playerCharacter?.experienceToNext || 100}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Points</span>
                    <span className="info-value">{playerCharacter?.statPoints || 0}</span>
                  </div>
                  <div className="info-item star">
                    <span className="info-label">✦</span>
                    <span className="info-value">{(playerCharacter?.starEssence || 0).toLocaleString()}</span>
                  </div>
                </div>
              </div>

              {/* XP */}
              <div className="dev-card wide">
                <div className="card-title">Experience</div>
                <div className="btn-grid cols-4">
                  <button className="dev-btn" onClick={() => addExperience(100)}>+100</button>
                  <button className="dev-btn" onClick={() => addExperience(1000)}>+1k</button>
                  <button className="dev-btn" onClick={() => addExperience(10000)}>+10k</button>
                  <button className="dev-btn primary" onClick={() => addExperience(100000)}>+100k</button>
                </div>
              </div>

              {/* Stats */}
              <div className="dev-card wide">
                <div className="card-title">Primary Stats</div>
                <div className="stats-grid">
                  {['STR', 'DEX', 'INT', 'LUK'].map(stat => (
                    <div key={stat} className="stat-item">
                      <span className="stat-name">{stat}</span>
                      <span className="stat-val">{playerCharacter?.primaryStats?.[stat] || 10}</span>
                      <button className="stat-btn" onClick={() => allocateStatPoint?.(stat)}>+</button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Star Essence */}
              <div className="dev-card wide star-card">
                <div className="card-title">✦ Star Essence</div>
                <div className="btn-grid cols-5">
                  <button className="dev-btn star" onClick={() => modifyStarEssence(100, false)}>+100</button>
                  <button className="dev-btn star" onClick={() => modifyStarEssence(1000, false)}>+1k</button>
                  <button className="dev-btn star" onClick={() => modifyStarEssence(10000, false)}>+10k</button>
                  <button className="dev-btn star" onClick={() => modifyStarEssence(50000, false)}>+50k</button>
                  <button className="dev-btn star" onClick={() => modifyStarEssence(100000, false)}>+100k</button>
                </div>
                <div className="btn-grid cols-3">
                  <button className="dev-btn" onClick={() => modifyStarEssence(0, true)}>Set 0</button>
                  <button className="dev-btn" onClick={() => modifyStarEssence(5000, true)}>Set 5k</button>
                  <button className="dev-btn" onClick={() => modifyStarEssence(50000, true)}>Set 50k</button>
                </div>
              </div>
            </div>
          )}

          {/* === WORLD TAB === */}
          {activeTab === 'world' && (
            <div className="dev-section">
              {/* Toggles */}
              <div className="dev-card wide toggles-large">
                <div className="toggle-large">
                  <div className="toggle-info">
                    <span className="toggle-icon">{devCooldownsDisabled ? '⚡' : '⏱️'}</span>
                    <span className="toggle-text">{devCooldownsDisabled ? 'Cooldowns DISABLED' : 'Cooldowns Active'}</span>
                  </div>
                  <button
                    className={`toggle-btn-large ${devCooldownsDisabled ? 'on' : ''}`}
                    onClick={() => setDevCooldownsDisabled(!devCooldownsDisabled)}
                  >
                    {devCooldownsDisabled ? 'ON' : 'OFF'}
                  </button>
                </div>
                <div className="toggle-large">
                  <div className="toggle-info">
                    <span className="toggle-icon">{devTeleportMode ? '🌀' : '🚶'}</span>
                    <span className="toggle-text">{devTeleportMode ? 'Teleport Mode' : 'Normal Movement'}</span>
                  </div>
                  <button
                    className={`toggle-btn-large ${devTeleportMode ? 'on' : ''}`}
                    onClick={() => setDevTeleportMode(!devTeleportMode)}
                  >
                    {devTeleportMode ? 'ON' : 'OFF'}
                  </button>
                </div>
              </div>

              {/* World Info */}
              <div className="dev-card wide info">
                <div className="info-grid cols-3">
                  <div className="info-item">
                    <span className="info-label">Current</span>
                    <span className="info-value small">{worldMap?.currentNode || 'kingdom'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Tiles</span>
                    <span className="info-value">{tilesCount}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Wonders</span>
                    <span className="info-value">{(worldMap?.discoveredWonderSites || []).length}</span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="dev-card wide">
                <div className="card-title">Actions</div>
                <div className="btn-grid cols-3">
                  <button className="dev-btn" onClick={resetCooldowns}>Reset Cooldowns</button>
                  <button className="dev-btn" onClick={() => discoverTiles(5)}>Discover +5</button>
                  <button className="dev-btn" onClick={() => discoverTiles(10)}>Discover +10</button>
                </div>
                <div className="btn-grid cols-3" style={{ marginTop: '0.5rem' }}>
                  <button
                    className="dev-btn"
                    onClick={() => setWorldMap(prev => ({ ...prev, devExplorationLimit: 100 }))}
                  >
                    🔓 Limit: 100
                  </button>
                  <button
                    className="dev-btn"
                    onClick={() => setWorldMap(prev => ({ ...prev, devExplorationLimit: 500 }))}
                  >
                    🔓 Limit: 500
                  </button>
                  <button
                    className="dev-btn primary"
                    onClick={() => setWorldMap(prev => ({ ...prev, devExplorationLimit: 9999 }))}
                  >
                    🔓 Limit: 9999
                  </button>
                </div>
                <div className="btn-grid cols-2">
                  <button className="dev-btn primary" onClick={() => setWorldMap(prev => ({ ...prev, currentNode: 'kingdom' }))}>
                    🏰 Go to Kingdom
                  </button>
                  <button className="dev-btn danger" onClick={() => {
                    if (window.confirm('Reset ALL game data?')) {
                      localStorage.clear();
                      window.location.reload();
                    }
                  }}>
                    🗑️ Reset All Data
                  </button>
                </div>
              </div>

              {/* Debug Info */}
              <div className="dev-card wide">
                <div className="card-title">Debug Info</div>
                <div className="debug-grid">
                  <span>Techs: {researchedTechs?.length || 0}</span>
                  <span>Quests: {completedQuests?.length || 0}</span>
                  <span>Wonders: {builtWonders?.length || 0}</span>
                  <span>Inv: {inventoryCount}/200</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default DevToolsPanel;
